<template>
  <div
    v-if="!isUserService"
    class="status-history">
    <a-divider>История изменений статусов</a-divider>

    <a-skeleton
      v-if="historyLoading"
      active
      :paragraph="{ rows: 7 }" />
    <a-empty
      v-if="!historyLoading && !timelineItems.length"
      :image="simpleImage" />

    <a-timeline v-if="!historyLoading && timelineItems.length">
      <a-timeline-item v-if="statusHistoryItems.length">
        <status-tag
          :status="getStatusTag(statusHistoryItems[0].from_status_id)"
          :style="{ width: '140px' }" />
      </a-timeline-item>
      <a-timeline-item
        v-for="(action, i) in timelineItems"
        :key="`${action.kind}-${i}`">
        <div v-if="action.kind === 'apartment_pause'">
          <a-tag color="orange">
            Пауза апартамента
          </a-tag>
          <span class="status-history__user">{{ action.apartment_name }}</span>
          <div class="status-history__user">
            с {{ moment(action.started_at).format('DD.MM.YYYY HH:mm:ss') }}
            по {{ action.ended_at ? moment(action.ended_at).format('DD.MM.YYYY HH:mm:ss') : 'настоящее время' }}
          </div>
        </div>
        <div v-else>
          <a-row
            flex
            :style="{ flexWrap: 'nowrap' }">
            <template v-if="action.from_status_id === action.to_status_id">
              <a-tag
                :color="action.successfully ? 'cyan' : 'red'"
                :style="{ cursor: 'pointer', maxWidth: '140px', minWidth: '125px', flex: 'none' }">
                <template #icon>
                  <send-outlined />
                </template>
                {{ action.successfully ? 'Отправлено' : 'Не отправлено' }}
              </a-tag>
            </template>

            <template v-else>
              <status-tag
                :status="getStatusTag(action.to_status_id)"
                :style="{ cursor: 'pointer', maxWidth: '140px', minWidth: '125px', flex: 'none' }" />
            </template>

            <span class="status-history__user">
              {{ moment(action.created_at).format('DD.MM.YYYY HH:mm:ss') }}
            </span>
          </a-row>
          <div
            v-if="action.reason === 'apartment_project_type'"
            class="status-history__user">
            {{ action.to_status_id === -1 ? '3D выключен при смене типа апартамента'
              : 'Восстановлен прежний статус 3D при смене типа апартамента' }}
          </div>
          <div
            v-else-if="action.reason === 'processing_type_selection'"
            class="status-history__user">
            {{ action.to_status_id === -1 ? '3D обработка выключена'
              : '3D обработка включена с восстановлением статуса' }}
          </div>
          <span
            class="status-history__user"
            :style="{ marginTop: '.25rem' }">
            {{ getCurrentUser(action.user_id) }}
          </span>
        </div>
      </a-timeline-item>

      <a-timeline-item
        v-if="workTime"
        color="#00CCFF">
        <template #dot>
          <smile-outlined />
        </template>
        <p>
          <i>Общее время работы:</i> <a-tag>{{ workTime }}</a-tag>
        </p>
      </a-timeline-item>
    </a-timeline>
  </div>
</template>

<script>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { first, last, filter } from 'lodash';
import { Empty } from 'ant-design-vue';
import {
  SmileOutlined,
  SendOutlined,
} from '@ant-design/icons-vue';
import moment from 'moment';

import StatusTag from '@/Components/Common/StatusTag.vue';
import { getStatusTag } from '@/enums/PlanStatusEnum';

export default {
  name: 'PlanStatusLogs',
  components: { SmileOutlined, SendOutlined, StatusTag },
  props: {
    id: {
      default: () => null,
      type: Number,
    },
    type: {
      default: () => null,
      type: Number,
    },
  },
  setup(props) {
    const store = useStore();
    const statusHistoryItems = computed(() => {
      const dates = [...store.state.crm.statusHistory.items];
      return dates.sort((a, b) => {
        const c = new Date(a.created_at);
        const d = new Date(b.created_at);
        return c - d;
      });
    });
    const timelineItems = computed(() => [
      ...statusHistoryItems.value.map((item) => ({ ...item, kind: 'status', at: item.created_at })),
      ...store.state.crm.statusHistory.apartmentPauses.map((period) => ({
        ...period,
        kind: 'apartment_pause',
        at: period.started_at,
      })),
    ].sort((a, b) => new Date(a.at) - new Date(b.at)));

    store.dispatch('crm/getStatusHistory', { id: props.id, type: props.type });

    return {
      getStatusTag,
      moment,
      simpleImage: Empty.PRESENTED_IMAGE_SIMPLE,

      getCurrentUser: (userId) => {
        const user = store.state.crm.simpleUsersList.find(({ id }) => id === userId);
        return user ? user.name : '';
      },

      isUserService: store.getters['auth/hasPermission']('ui.action.components_crm_plans_statuslogs.7'),
      historyLoading: computed(() => store.state.crm.statusHistory.isLoading),
      statusHistoryItems,
      timelineItems,
      workTime: computed(() => {
        const history = statusHistoryItems.value;
        if (!history.length) {
          return null;
        }

        const startDate = moment(first(history).created_at);
        const endDate = moment(last(history).created_at);

        const duration = endDate.isSame(startDate)
          ? moment.duration(moment().diff(startDate))
          : moment.duration(endDate.diff(startDate));

        return filter([
          { num: duration.seconds(), label: 's' },
          { num: duration.minutes(), label: 'min' },
          { num: duration.hours(), label: 'h' },
          { num: duration.days(), label: 'd' },
          { num: duration.months(), label: 'm' },
        ], 'num')
          .reverse()
          .map(({ num, label }) => num + label)
          .join(' ');
      }),
    };
  },
};
</script>

<style scoped>
.status-history {
  width: calc(100% / 3);
}
@media (max-width: 767px) {
  .status-history {
    width: 100%;
  }
}
</style>
